import type { DeclarationSnapshot, EmployeeSectionStep } from './employeeSectionTypes';
import { EMPLOYEE_SECTION_STEP_ORDER } from './employeeSectionTypes';

export function stepQuestionKey(step: EmployeeSectionStep): string {
  return `submitDocuments.dependentDeduction.employee.steps.${step}`;
}

export function stepIndex(step: EmployeeSectionStep): number {
  return EMPLOYEE_SECTION_STEP_ORDER.indexOf(step);
}

export function nextStepAfter(
  current: EmployeeSectionStep,
  decl: DeclarationSnapshot
): EmployeeSectionStep | null {
  switch (current) {
    case 'fullName':
      return 'fullNameKana';
    case 'fullNameKana':
      return 'dob';
    case 'dob':
      return 'myNumber';
    case 'myNumber':
      return 'address';
    case 'address':
      return 'headOfHousehold';
    case 'headOfHousehold':
      return decl.isHeadOfHousehold === 'no' ? 'headName' : 'employeeEstimatedIncome';
    case 'headName':
      return 'headRelationship';
    case 'headRelationship':
      return 'employeeEstimatedIncome';
    case 'employeeEstimatedIncome':
      return 'multipleEmployers';
    case 'multipleEmployers':
      return decl.multipleEmployers === 'yes' ? 'multipleEmployersNotice' : null;
    case 'multipleEmployersNotice':
      return null;
    default:
      return null;
  }
}

export function prevStepForUndo(
  current: EmployeeSectionStep,
  decl: DeclarationSnapshot
): EmployeeSectionStep | null {
  switch (current) {
    case 'fullName':
      return null;
    case 'fullNameKana':
      return 'fullName';
    case 'dob':
      return 'fullNameKana';
    case 'myNumber':
      return 'dob';
    case 'address':
      return 'myNumber';
    case 'headOfHousehold':
      return 'address';
    case 'headName':
      return 'headOfHousehold';
    case 'headRelationship':
      return 'headName';
    case 'employeeEstimatedIncome':
      return 'headRelationship';
    case 'multipleEmployers':
      return 'employeeEstimatedIncome';
    case 'multipleEmployersNotice':
      return 'multipleEmployers';
    default:
      return null;
  }
}

export function isEmployeeSectionComplete(step: EmployeeSectionStep, decl: DeclarationSnapshot): boolean {
  if (step === 'multipleEmployers' && decl.multipleEmployers === 'no') return true;
  if (step === 'multipleEmployersNotice') return true;
  return false;
}

/** Branch steps that were skipped based on prior answers — hide from chat history. */
export function isSkippedStep(step: EmployeeSectionStep, decl: DeclarationSnapshot): boolean {
  if (step === 'headName' && decl.isHeadOfHousehold !== 'no') return true;
  if (step === 'headRelationship' && decl.isHeadOfHousehold !== 'no') return true;
  if (step === 'multipleEmployersNotice' && decl.multipleEmployers !== 'yes') return true;
  return false;
}

export function shouldShowStepInHistory(
  step: EmployeeSectionStep,
  committed: boolean,
  decl: DeclarationSnapshot
): boolean {
  return committed && !isSkippedStep(step, decl);
}

const BRANCH_GATE_STEPS: EmployeeSectionStep[] = ['headOfHousehold', 'multipleEmployers'];

export function isBranchGateStep(step: EmployeeSectionStep): boolean {
  return BRANCH_GATE_STEPS.includes(step);
}

export function reviewStep(decl: DeclarationSnapshot): EmployeeSectionStep {
  return decl.multipleEmployers === 'yes' ? 'multipleEmployersNotice' : 'multipleEmployers';
}

/** First step still missing after a branch gate edit; null when the branch is complete. */
export function firstMissingBranchStep(
  gateStep: EmployeeSectionStep,
  decl: DeclarationSnapshot,
  opts: { noticeAcknowledged: boolean }
): EmployeeSectionStep | null {
  if (gateStep === 'headOfHousehold' || gateStep === 'headName' || gateStep === 'headRelationship') {
    if (decl.isHeadOfHousehold !== 'no') return null;
    if (!decl.headOfHouseholdName.trim()) return 'headName';
    if (!decl.relationshipToHead) return 'headRelationship';
    return null;
  }
  if (gateStep === 'multipleEmployers' || gateStep === 'multipleEmployersNotice') {
    if (decl.multipleEmployers === 'yes' && !opts.noticeAcknowledged) return 'multipleEmployersNotice';
    return null;
  }
  return null;
}
